<script setup lang="ts">
import type { ProRouteRaw, ProSettableOption } from '@inn/components';
import type { VoSystems } from '@/apis/permits';
import { useConfirm, XomProLayout } from '@inn/components';
import { createVNode, h, markRaw, onBeforeMount, onBeforeUpdate, ref } from 'vue';
import { useRouter } from 'vue-router';
import { IS_DEV, IS_PRERELEASE, IS_PROD } from '@inn/const';
import { notification } from 'ant-design-vue';
import { NotificationOutlined } from '@ant-design/icons-vue';
import { debounce } from 'radash';
import { useTokenStore } from '@/storage/tokens';
import { useProfileStore } from '@/storage/profile';
import { usePermitStore } from '@/storage/permits';
import { useNavStore } from '@/storage/nav';
import { parseStatefulLink, usePermitChangeConfirm } from '@/utils';
import { useFooterStore } from '@/storage/footer';
import { useNotifySdk } from '@/hooks';
import { addSystemRoutes } from '@/router/guard';
import { useNotifyStore } from '@/storage/notify';
import NotifyContent from '@/components/xom-message-modal/notify-content.vue';
import { confirmNotify } from '@/apis/notify';

defineOptions({ name: 'XomMainRoot' });

const router = useRouter();

const nav = useNavStore();
const tokens = useTokenStore();
const profile = useProfileStore();
const permits = usePermitStore();
const footerState = useFooterStore();
const notify = useNotifyStore();

const notifySdk = useNotifySdk();

const devModel = ref<VoSystems>();
const devModalOpen = ref(false);

const invariableSettings = markRaw<ProSettableOption>({
  showFoot: false,
  menusSplit: false,
  showWatermark: false,
});

if (IS_PROD) {
  Object.assign(invariableSettings, {
    // theme: 'light',
    // body: 'fluid',
    // compact: false,
    // adaptive: false,
    // fixedSide: true,
  });
}

const permissionUpdate = debounce({ delay: 1000 }, async () => {
  await profile.reload();
  await permits.reload();
  if (IS_PRERELEASE) {
    usePermitChangeConfirm('sse', () => {
      router.go(0);
    });
  } else {
    //做了一次静默刷新菜单的操作
    nav.emptyMenus();
    addSystemRoutes(router, permits.systems, [], profile.site.isLiarVisible);
  }
});

notifySdk.addEventListener('USER-PERMISSION-UPDATE', () => {
  permissionUpdate();
});

notifySdk.addEventListener('message', async (data) => {
  const notifyData = data.data;
  notify.setUnreadNum(notifyData.unConfirmCount);
  if (notifyData.eventNotifyType === 'DISPLAY') {
    if (notifyData.forcePublish) {
      useConfirm('info', {
        title: notifyData.eventName ?? '消息通知',
        width: 'md',
        content: createVNode(NotifyContent, { contentHtml: notifyData.eventContent }),
        okText: '我已知晓',
        onOk: async () => {
          notifyData.id && (await confirmNotify(notifyData.eventUserMessageId));
        },
      });
    } else {
      notification.open({
        message: '消息通知',
        description:
          notifyData.eventBusinessType === 'SYSTEM'
            ? '您收到1条新的平台消息'
            : `您收到1条新的${notifyData.eventName}`,
        icon: () => h(NotificationOutlined, { class: 'notify-icon' }),
      });
    }
  }
});

notifySdk.addEventListener('CLIENT_TOGGLE_SITE_SIGNAL', async () => {
  window.location.href = '/';
});

/**
 * Make sure tokens authorized.
 */
async function ensureAuthorized() {
  if (tokens.authed) {
    return;
  }
  if (tokens.flushable) {
    return await tokens.flush();
  }
  await tokens.deauth({ originalUrl: location.origin });
}

async function rmvDevEntry(path: string) {
  permits.rmvDevEntry(path);
  await permits.reload();
  router.go(0);
}

function openDevModal(raw: ProRouteRaw) {
  const _raw = permits.systems.find((s_) => s_.activeRule === raw.path);
  if (!_raw) {
    return;
  }
  devModalOpen.value = true;
  const { key, activeRule, entry: path, label: name, type: systemType, devEntry } = _raw;
  devModel.value = { key, activeRule, path: devEntry || path, name, nodeName: name, systemType };
}

onBeforeMount(async () => {
  await ensureAuthorized();
  await profile.confSite();
});

onBeforeUpdate(ensureAuthorized);
</script>

<template>
  <x-config-provider :access="permits.features">
    <xom-pro-layout
      :title="profile.site?.name"
      :logo-pure="profile.site?.logoPure"
      :logo-text="profile.site?.logoText"
      :menus-data="nav.menus"
      :menus-path-formatter="parseStatefulLink"
      :default-setting="profile.preference"
      :unsettable="invariableSettings as any"
      :footer-config="{
        setRef: footerState.setFooterRef,
        footerRef: footerState.footerRef,
        isFooterShow: footerState.isFooterShow,
        footerOptions: {
          ...footerState.footerOptions,
        },
      }"
    >
      <template #header-after>
        <xom-head-actions />
      </template>

      <template #menu-title="menuRaw">
        {{ menuRaw.meta?.title }}
        <!--  Todo menuRaw.meta缺少type字段 -->
        <edit-outlined
          v-if="menuRaw.meta?.devEntry && IS_DEV"
          style="position: relative; z-index: 2; margin-left: 0.5em"
          @click.prevent.stop="() => openDevModal(menuRaw)"
        />

        <close-outlined
          v-if="menuRaw.meta?.devEntry && IS_DEV"
          style="position: relative; z-index: 2"
          @click.prevent.stop="() => rmvDevEntry(menuRaw.path)"
        />
      </template>

      <template #setting-drawer-footer>
        <xom-profile-saver />
      </template>

      <router-view class="xom-apps-main" />

      <xom-system-form-modal v-model:open="devModalOpen" :dto="devModel" type="develop" />
    </xom-pro-layout>
  </x-config-provider>
</template>
